import * as React from 'react';
import { useSelector } from 'react-redux';
import { IOpeningHours } from '@pepperhq/types-sdk';
import { OrderScenario } from 'components/order/model/Order';
import { isDefined } from 'lib/typeInference';
import { ApplicationState } from 'store/store';
import { Location } from '../model/Location';
import { getWorkingHoursSummary, resolveTimezone } from '../openingHoursHelpers';

interface ILocationHelper {
    isWorkingHour: boolean;
    isLocationOpen: boolean;
    isLocationOrderEnabled: boolean;
    currentLocation: Location | undefined;
    checkServiceAvailability: (scenario: OrderScenario | OrderScenario[]) => boolean;
    verifyLocationOpeningHours: () => boolean;
}

export function isLocationOpen(
    openingHours?: IOpeningHours<string>,
    timezone?: string,
    defaultIsOpen = false
): boolean {
    if (!isDefined(openingHours)) {
        return defaultIsOpen;
    }
    return getWorkingHoursSummary(openingHours, resolveTimezone(timezone)).isOpenNow;
}

export function useLocationHelpers(): ILocationHelper {
    const location = useSelector((state: ApplicationState) => state.locations.currentLocation);
    const settings = useSelector((state: ApplicationState) => state.settings.settings);
    const { timeSlot } = useSelector((state: ApplicationState) => state.basket);
    const checkServiceAvailability = React.useCallback(
        (scenario: OrderScenario | OrderScenario[]) => {
            if (location && location.services) {
                const serviceChecker = (orderScenario: OrderScenario) => {
                    switch (orderScenario) {
                        case OrderScenario.ORDER_TO_TABLE:
                            return (
                                !!location.services?.order_to_table && (settings?.orderToTableEnabled ?? true)
                            );
                        case OrderScenario.PREORDER:
                            return !!location.services?.pre_order && (settings?.preOrderEnabled ?? true);
                        case OrderScenario.TABLE:
                            return !!location.services?.pay_at_table && (settings?.payAtTableEnabled ?? true);
                        case OrderScenario.DELIVERY:
                            return !!location.services?.delivery && !!settings?.deliveryProvider;
                        case OrderScenario.DINE_IN:
                            return !!location.services?.dine_in && (settings?.dineInEnabled ?? true);
                        default:
                            return false;
                    }
                };
                if (Array.isArray(scenario)) {
                    return scenario.some(sc => serviceChecker(sc) === true);
                }

                return serviceChecker(scenario);
            }

            return true;
        },
        [
            location,
            settings?.deliveryProvider,
            settings?.orderToTableEnabled,
            settings?.payAtTableEnabled,
            settings?.preOrderEnabled,
            settings?.dineInEnabled
        ]
    );
    const timezone = resolveTimezone(location?.timezone, settings?.region.timezone);
    const verifyLocationOpeningHours = React.useCallback(
        () => isLocationOpen(location?.openingHours, timezone),
        [location?.openingHours, timezone]
    );
    const _isLocationOpen = React.useMemo(
        () => isLocationOpen(location?.openingHours, timezone),
        [location?.openingHours, timezone]
    );
    const isWorkingHour = React.useMemo(() => _isLocationOpen || !!timeSlot, [_isLocationOpen, timeSlot]);
    const isLocationOrderEnabled = React.useMemo(
        () => (settings?.orderingEnabled !== undefined ? settings && settings.orderingEnabled : true),
        [settings]
    );

    return {
        isWorkingHour,
        isLocationOpen: _isLocationOpen,
        verifyLocationOpeningHours,
        isLocationOrderEnabled,
        currentLocation: location,
        checkServiceAvailability
    };
}
